<template>
	<Accordion
		variant="accordion--vertical"
		:isExpandable="totalCompanies > 1 || hasLocations"
		:isOpen="isOpenStatus"
		@expanded="onAccordionExpanded"
		ref="accordion"
	>
		<template #main>
			<Card class="customer-card" :highlights="exactMatch" ref="card">
				<template #content>
					<div class="customer-card__info">
						<div class="customer-card__favorite">
							<input
								type="checkbox"
								:id="`${uid}_input`"
								class="customer-card__favorite-icon"
								aria-label="Toggle favorite status"
								title="Toggle favorite status"
								:value="customer.favorite"
								:checked="customer.favorite"
								@input="updateFavorite"
								ref="favorite"
							/>
						</div>
						<div class="customer-card__content">
							<div class="customer-card__content-details">
								<div class="customer-card__name-copy">
									<span
										class="customer-card__name"
										data-test="customerName"
										v-html="displayCustomerName"
									/>
									<!-- DEV NOTE: update when signalr setup is ready -->
									<div
										class="customer-card__copy"
										v-if="copiedDB"
										data-test="refreshDbCopy"
									>
										<div
											class="customer-card__refresh-date"
											v-if="!refreshCopyInProgress"
										>
											<Button
												class="btn--icon-only-circle"
												iconClassName="icon-refresh"
												:iconBeforeLabel="true"
												:btnClick="
													() => {
														refreshModalDisplay = true;
													}
												"
												data-test="refreshCopyBtn"
											>
											</Button>
											<span
												class="customer-card__last-refresh-date"
											>
												Last Refresh:
												{{ lastCopyDate }}
											</span>
										</div>
										<div
											class="customer-card__refresh-date-progress"
											v-if="refreshCopyInProgress"
										>
											<!-- UI/UX: please add the coping spinner to the spinner variant component. Update the component styles as needed -->
											<Spinner
												variant="infinity-scroll"
												:processor="{ loading: true }"
											/>
											<span
												class="customer-card__refresh-progress"
											>
												Refresh Copy in Progress
											</span>
										</div>
									</div>
								</div>

								<div class="customer-card__number-and-tags">
									<span class="customer-card__number">
										#{{ customer.dealershipGroupNumber }}
									</span>
									<span
										class="customer-card__tags"
										data-test="tagLabels"
									>
										<TagLabel
											v-if="totalCompanies > 0"
											:message="
												`Companies (${totalCompanies})`
											"
											:state="'neutral'"
											ref="totalCompaniesTagLabel"
										/>
										<TagLabel
											v-if="hasLocations"
											:message="
												`Sub Locations (${totalLocations()})`
											"
											:state="'neutral'"
											ref="totalLocationsTagLabel"
										/>
									</span>
								</div>
							</div>
							<div
								v-if="showBetaFeaturesDiv"
								class="customer-card__beta-features"
								ref="customerCardFeature"
							>
								<span
									class="customer-card__beta-features-image"
								>
									<i class="icon icon-beta-features"></i>
								</span>
								<div class="customer-card__beta-features-data">
									<span
										class="customer-card__beta-features-data-text"
									>
										Beta Feature -
										{{ renderFeatureDescription }}
									</span>

									<Button
										v-if="showMoreLessButton"
										className="btn--inline-link"
										:labelText="
											!betaFeatureMoreLessTextButton
												? 'More'
												: 'Less'
										"
										:btnClick="showMore"
									/>
								</div>
							</div>
						</div>
					</div>
				</template>
				<template #actions>
					<ActionsSet
						class="customer-card__actions"
						:id="`${uid}_action`"
						:variant="variant"
						:options="actions"
					/>
					<Modals
						:display="true"
						@show="refreshModalDisplay = false"
						v-if="refreshModalDisplay"
						type="confirmation"
						title="Are you sure you want to refresh the copy data?"
						data-test="refreshCopyModal"
						labelConfirmButton="Yes, Refresh Copy Data"
						labelCancelButton="Cancel"
						@confirm="refreshCopyData"
						@cancel="refreshModalDisplay = false"
					>
						<template slot="modal-content">
							<p>
								This will copy the data from Production and
								apply it to this copied version of the client
							</p>
							<span
								>Copy last Refreshed on {{ lastCopyDate }}</span
							>
						</template>
					</Modals>
				</template>
				<template #footer>
					<ZoneIndicator
						v-if="
							customer.environmentName &&
								showZoneIndicator(customer.environmentId)
						"
						class="customer-card__indicator"
						:message="customer.environmentName"
						:state="zoneColors(customer.environmentId)"
						:hasZonePermission="hasZonePermission"
						ref="zoneIndicator"
					/>
				</template>
			</Card>
		</template>
		<template #body-content>
			<ul
				v-if="totalCompanies > 1"
				class="customer-card__company-card-list"
			>
				<li
					class="customer-card__company-card-list-item"
					v-for="(company, index) in customer.companies"
					:key="company.id"
					:style="{
						zIndex: customer.companies.length - index
					}"
				>
					<CompanyCard
						:company="company"
						:customer="customer"
						ref="company"
					/>
				</li>
			</ul>
			<ul
				v-if="totalCompanies <= 1 && hasLocations"
				class="customer-card__company-card-list"
			>
				<li
					v-for="(location, index) in customer.companies[0].locations"
					:key="location.id"
					class="customer-card__company-card-list-item"
					:style="{
						zIndex: customer.companies[0].locations.length - index
					}"
				>
					<LocationCard
						:location="location"
						:customer="customer"
						ref="locationCard"
					/>
				</li>
			</ul>
		</template>
	</Accordion>
</template>

<script>
import Accordion from '@/components/shells/accordion/accordion';
import Card from '@/components/shells/card/card';
import CompanyCard from './company-card';
import LocationCard from './location-card';
import TagLabel from '@/components/visual/tag-label';
import ActionsSet from '@/site-components/actions-set/actions-set';
import Button from '@/components/forms/button/button';
import CustomerService from '@/services/customers-service';
import CustomerCardActions from '@/mixins/customers/customer-card-action';
import Modals from '@/components/shells/modal/modals';
import ZoneIndicator from '@/components/visual/zone-indicator';

// const
import { MODULE_NAME } from '@/sections/options/options-mutation-type.js';

//libraries
import _ from 'lodash';
import { createTextValueObj } from '@/utils/object';

//mixins
import ZoneColors from '@/mixins/zoneColors';
import AccordionStatus from './accordions-status-mixin';
import AuthorizationMixin from '@/mixins/authorization/authorization-mixin.js';

//vuex
import { mapGetters } from 'vuex';

export default {
	name: 'CustomerCard',
	components: {
		Accordion,
		Card,
		CompanyCard,
		LocationCard,
		TagLabel,
		ActionsSet,
		Button,
		Modals,
		ZoneIndicator
	},
	mixins: [CustomerCardActions, ZoneColors, AccordionStatus,AuthorizationMixin],
	props: {
		customer: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			fullFeatureDescription: '',
			featureDescription: '',
			showMoreOrLessButton: false,
			betaFeatureMoreLessTextButton: false,
			refreshModalDisplay: false,
			refreshCopyInProgress: false,
			refreshLastDate: new Date('09/07/2022 02:40:09'),
			copiedDB: false
		};
	},
	created() {
		if (!this.customer.companies.length) {
			return;
		}

		if (this.$route.query.search) {
			this.onAccordionExpanded(true);
		} else {
			const hasAccordionStoredStatus = this.openStatusStored(
				'customersAccordionStatuses',
				this.customer.id
			);

			if (hasAccordionStoredStatus) {
				this.isOpenStatus = true;
			}
		}
	},
	computed: {
		...mapGetters(MODULE_NAME, ['showZoneIndicator']),
		hasCompanies() {
			if (this.totalCompanies > 0) {
				return true;
			}
			return false;
		},
		hasLocations() {
			return this.totalLocations() > 0;
		},
		displayCustomerName() {
			const name =
				this.customer.displayName || this.customer.dealershipGroup;
			return createTextValueObj(name, this.$route.query.search);
		},
		exactMatch() {
			return (
				this.$route.query.search &&
				this.customer.displayName === this.$route.query.search
			);
		},
		hasZonePermission() {
			if (this.isInternalUser) {
				// mocked permission. Update when zone permission is ready
				return this.validatePermission('zones.read') || true;
			}
			return false;
		},
		showMoreLessButton() {
			return this.showMoreOrLessButton;
		},
		showBetaFeaturesDiv() {
			return (
				_.get(this, 'customer.features.length', 0) > 0 &&
				this.isInternalUser
			);
		},
		renderFeatureDescription() {
			if (this.fullFeatureDescription) {
				if (
					(this.showMoreOrLessButton ||
						_.get(this, 'this.featureDescription', '') != '') &&
					!this.betaFeatureMoreLessTextButton
				) {
					return this.featureDescription;
				} else {
					return this.fullFeatureDescription;
				}
			}
			return '';
		},
		customerData() {
			const user = this.$store.getters['oidcStore/oidcUser'];
			return {
				customerId: this.customer.id,
				userId: user.sub,
				email: user.Email
			};
		},
		lastCopyDate() {
			return this.refreshLastDate.toLocaleString('en-us', {
				timeZoneName: 'shortGeneric'
			});
		},
		totalCompanies() {
			return this.customer.companies.length;
		}
	},
	methods: {
		showMore(e) {
			if (e) {
				e.preventDefault();
			}
			this.betaFeatureMoreLessTextButton = !this
				.betaFeatureMoreLessTextButton;
		},
		async updateFavorite(e) {
			e.preventDefault();
			let reload = false;
			let customer = { ...this.customer };
			customer.favorite = e.target.checked;

			if (this.$store.getters['customers/favoriteToggle']) {
				if (e.target.checked) {
					customer.favoriteClicked = true;
				} else if (!customer.favoriteClicked) {
					reload = true;
				}
			}
			await CustomerService.UpdateFavorite(customer).then(() => {
				if (reload) {
					this.$store.commit('customers/SetReloadCustomers', true);
				} else {
					this.$store.dispatch('customers/updateCustomer', customer);
				}
			});
		},
		/* TODO */
		refreshCopyData() {
			this.refreshModalDisplay = false;
			// complete when signalr is implemented
			this.refreshCopyInProgress = true;
			setTimeout(() => {
				this.refreshCopyInProgress = false;

				// display toast
				this.$store.dispatch('toastMessages/show', {
					type: 'success',
					message: `The Copy of ${this.customer.displayName} data was succefull.`,
					useIcon: true,
					userIcon: true,
					userDismissable: true
				});
			}, 3000);
		},
		onAccordionExpanded(status) {
			this.updateAccordionStatuses({
				id: this.customer.id,
				status,
				name: 'customersAccordionStatuses'
			});

			this.isOpenStatus = status;
		},
		totalLocations() {
			let count = 0;
			for (let i = 0; i < this.customer.companies.length; i++) {
				const company = this.customer.companies[i];
				if (company.locations) {
					count += company.locations.length;
				}
			}
			return count;
		}
	},
	watch: {
		customer: {
			deep: true,
			immediate: true,
			handler(obj) {
				if (!_.isEmpty(obj)) {
					this.$nextTick(() => {
						this.showMoreOrLessButton = false;
						if (this.$refs.customerCardFeature) {
							const PIXEL_TO_CHARACTER = 0.125;
							const clientWidth = this.$refs.customerCardFeature
								.clientWidth;

							const pixelToCharacterLen =
								clientWidth * PIXEL_TO_CHARACTER;

							if (obj.features.length) {
								const beta = 'Beta Feature - ';
								const fullDescription = obj.features.join(', ');

								if (
									beta.length + fullDescription.length >
									pixelToCharacterLen
								) {
									this.showMoreOrLessButton = true;
									const INITIAL_POSITION = 0;
									const FINAL_POSITION =
										beta.length > pixelToCharacterLen
											? 0
											: pixelToCharacterLen - beta.length;

									this.featureDescription = fullDescription.slice(
										INITIAL_POSITION,
										FINAL_POSITION
									);
								}
								this.fullFeatureDescription = fullDescription;
							}
						}
					});
				}
			}
		}
	}
};
</script>

<style lang="scss">
/**
    * Customer Card
    * Can use SCSS variables = TRUE
 */

@import '@/scss/5-objects/_cards.scss';
@import '@/scss/5-objects/_form.scss';

.customer-card {
	@include card('indicator');
	@include card('accordion');
	flex-wrap: wrap;
	display: grid;
	grid-template-columns: 1fr auto;

	&__info {
		@include mq($from: md) {
			flex: 2 1;
		}
	}

	&__favorite {
		height: 100%;
		margin-right: $gutter-md;
		@include mq($from: md) {
			float: left;
		}

		&-icon {
			appearance: none;
			outline: none;
			border: none;
			cursor: pointer;
			color: inherit;
			width: 19px;
			text-align: center;

			&::before {
				font-family: 'vue-support-icons';
				content: '\e929';
			}

			&:focus {
				outline: 1px solid;
			}

			&:active {
				background-color: transparent;
				color: inherit;
			}

			&:checked {
				&::before {
					color: $card-favorite;
					content: '\e92b';
				}
			}
		}
	}

	&__content {
		display: flex;
		flex-direction: column;
		@include mq($until: md) {
			width: 100%;

			&-details {
				display: inherit;
			}
		}
	}

	&__name-copy {
		display: flex;
		align-items: center;
	}

	&__name {
		font-weight: $medium-font-weight;
		@include mq($until: md) {
			@include ellipsis;
			display: block;
			margin-right: $gutter-sm;

			&:focus,
			&:hover,
			&:active {
				white-space: normal;
				width: auto;
				overflow: visible;
			}
		}

		@include heading('md');
		text-transform: capitalize;
	}

	/* &__number {
		font-size: $default-font-size - 2;
		@include mq($until: md) {
			margin-left: auto;
		}
		@include mq($from: md) {
			display: block;
			margin-top: $gutter-md;
		}
	} */

	&__beta-features {
		display: flex;
		gap: $gutter-sm;
		font-size: $default-font-size - 3;
		margin-top: $gutter-md;
		align-items: baseline;

		&-image {
			color: $white;
			display: flex;
			justify-content: center;
			align-items: center;
			margin-top: 1px;
			flex: 0 0 16px;
			@include create-circle(16px, 16px, $ui-positive);

			.icon {
				font-size: $default-font-size - 6;
			}
		}

		&-more {
			display: flex;
			margin-top: $gutter-sm;
		}

		&-data {
			&-text {
				display: flex;
			}
		}
	}

	&__actions {
		@include mq($until: md) {
			margin-top: $gutter-md;
		}
		@include mq($from: md) {
			align-self: center;
		}
	}

	&__company-card-list {
		display: flex;
		flex-direction: column;
	}

	&__company-card-list-item {
		margin-top: $gutter-lg;

		&:last-child {
			margin-bottom: $gutter-lg;
		}
	}

	&__number-and-tags,
	&__tags {
		display: flex;
		align-items: center;
	}

	&__number-and-tags {
		gap: $gutter-lg;
		margin-top: $gutter-sm;
	}

	&__number {
		@include body-text('regular');
	}

	&__tags {
		gap: $gutter-md;
	}
}
</style>
